import { useState, useMemo, useEffect } from "react";
import Modal, { ModalSize } from "./Modal";
import { useForm } from "@/features/tutor/context/FormContext";
import { useModalStore, ModalType } from "@/store/useModalStore";
import { CHARACTERS, type Character } from "@/constants/character";
import { CHARACTER_ICONS } from "@/assets/icons/characters";
import "./lesson-entry.css";

interface ChangeCharacterModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnModalType?: ModalType;
  returnModalProps?: Record<string, unknown>;
}

const ChangeCharacterModal = ({
  isOpen,
  onClose,
  returnModalType = ModalType.SESSION_START,
  returnModalProps,
}: ChangeCharacterModalProps) => {
  const { formData, updateField } = useForm();
  const { openModal, closeModal } = useModalStore();

  const [selectedId, setSelectedId] = useState<string>(
    formData.character?.characterId ?? CHARACTERS[0].id,
  );

  useEffect(() => {
    if (isOpen) {
      const committedId = formData.character?.characterId ?? CHARACTERS[0].id;
      // If the committed character was removed from CHARACTER_ICONS in a later
      // build it won't appear in the grid — fall back to the first visible one.
      const isVisible = visibleCharacters.some((c) => c.id === committedId);
      setSelectedId(
        isVisible
          ? committedId
          : (visibleCharacters[0]?.id ?? CHARACTERS[0].id),
      );
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole bundle goes back untouched. Rebuilding it field by field is what
  // used to drop the caller's assessment props on the way home.
  const getReturnModal = () => ({
    type: returnModalType,
    props: returnModalProps || {},
  });

  const visibleCharacters = useMemo(() => {
    const committedId = formData.character?.characterId;
    // Move committed character to front so it survives icon-based deduplication
    // regardless of its position in the CHARACTERS array.
    let list = CHARACTERS;
    if (committedId) {
      const idx = list.findIndex((c) => c.id === committedId);
      if (idx > 0)
        list = [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
    }
    // Deduplicate by icon URL so visually identical avatars only appear once.
    // Committed character is at front so it always wins dedup.
    // The !icon guard handles stale localStorage: if a character ID was removed
    // from CHARACTER_ICONS in a later build, it is silently excluded from the grid.
    const seenIcons = new Set<string>();
    list = list.filter((c) => {
      const icon = CHARACTER_ICONS[c.id];
      if (!icon || seenIcons.has(icon)) return false;
      seenIcons.add(icon);
      return true;
    });
    // Pin committed character to top so it stays stable while user browses.
    const committed = list.find((c) => c.id === committedId);
    if (committed) {
      return [committed, ...list.filter((c) => c.id !== committedId)];
    }
    return list;
  }, [formData.character?.characterId]);

  const handleConfirm = () => {
    updateField("character", { characterId: selectedId });
    const nextModal = getReturnModal();
    closeModal();
    openModal(nextModal.type, nextModal.props);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      size={ModalSize.wide}
      className="et-lesson-entry-modal et-entry-picker et-entry-picker-wide p-0"
    >
      <div className="flex h-full min-h-0 w-full flex-col">
        {/* Topbar */}
        <div className="flex flex-shrink-0 items-center gap-3 px-6 pt-5 pb-3">
          <button
            type="button"
            onClick={() => {
              const nextModal = getReturnModal();
              openModal(nextModal.type, nextModal.props);
            }}
            className="flex-none w-[40px] h-[40px] rounded-[6px] bg-[#edf0e5] text-[#28513e] border-none cursor-pointer flex items-center justify-center transition-colors hover:bg-[#dfe7d7]"
            aria-label="Back"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[18px] h-[18px]"
            >
              <path d="M10 18 L4 12 L10 6" />
              <path d="M4 12 H20" />
            </svg>
          </button>
          <h2 className="font-libre-baskerville text-[20px] text-[#28513e]">
            Choose your avatar
          </h2>
        </div>

        {/* Step hint */}
        <div className="flex flex-shrink-0 items-center gap-2 px-6 pb-3 text-[13.5px] text-[#637264]">
          <span className="flex-none inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#EEF0E3] text-[#28543D]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-3 h-3"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
            </svg>
          </span>
          <span>
            Pick the face you want to see during your lessons. Independent of
            the voice.
          </span>
        </div>

        {/* Face grid */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-3 pb-4">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-6 gap-y-5 justify-items-center">
            {visibleCharacters.map((character: Character) => {
              const isSelected = character.id === selectedId;
              return (
                <button
                  key={character.id}
                  type="button"
                  onClick={() => setSelectedId(character.id)}
                  aria-label={`Pick ${character.name}`}
                  className="relative flex items-center justify-center rounded-full border-none bg-transparent p-0 cursor-pointer transition-transform"
                  style={{
                    width: 80,
                    height: 80,
                    transform: isSelected ? "scale(1.06)" : undefined,
                  }}
                >
                  <span
                    className="block w-full h-full rounded-full overflow-hidden"
                    style={{
                      boxShadow: isSelected
                        ? "0 0 0 3px #28543D, 0 0 0 6px rgba(40,84,61,0.15)"
                        : "inset 0 0 0 1px rgba(0,0,0,0.06)",
                    }}
                  >
                    <img
                      src={CHARACTER_ICONS[character.id]}
                      alt={character.name}
                      className="w-full h-full object-cover rounded-full"
                      draggable={false}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-shrink-0 justify-center px-6 pt-3 pb-5">
          <button
            type="button"
            onClick={handleConfirm}
            className="et-primary min-w-[260px] px-6 shadow-[0_10px_26px_-10px_rgba(40,84,61,0.6)]"
          >
            Use this Avatar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeCharacterModal;
